import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import {
  getMatch,
  listMatchLeaderboards,
  listStageLeaderboards,
} from "../../lib/leaderboardApi";
import {
  DEFAULT_LEADERBOARD_BANNER_SRC,
  preloadLeaderboardBanner,
} from "./leaderboardBannerPreload";
import { BoothLeaderboardDisplay } from "./LeaderboardMatchDetail";
import "./Leaderboard.css";

const SHOW_MATCH_ID = "38B7BA23-37B4-4970-BAB4-AE48EC52B588";
const SHOW_MATCH_ID_SECOND = "82626763-4714-44C5-BFF3-824625F61D06";
const DEFAULT_TOP_COUNT = "all";
const SHOW_PLAYLIST = [
  {
    key: "arc-250-logo",
    type: "image",
    src: "/ARC_250_logo.png",
    label: "ARC 250",
    caption: "Rotating booth slide between live standings",
    durationMs: 10000,
  },
  {
    key: "visit-us",
    type: "image",
    src: "/VisitUs.png",
    label: "Visit the booth",
    caption: "Public-facing booth prompt for NRA show attendees",
    durationMs: 5000,
  },
  {
    key: "leaderboard-a",
    type: "leaderboard",
    matchId: SHOW_MATCH_ID,
    durationMs: 20000,
    topCount: DEFAULT_TOP_COUNT,
  },
  {
    key: "nra-video",
    type: "video",
    src: "/NRAVideo_V1.mov",
  },
  {
    key: "leaderboard-b",
    type: "leaderboard",
    matchId: SHOW_MATCH_ID_SECOND,
    durationMs: 20000,
    topCount: DEFAULT_TOP_COUNT,
  },
];

function sortByTimeAsc(rows, field) {
  return [...rows].sort((a, b) => {
    const av = Number.isFinite(Number(a?.[field]))
      ? Number(a[field])
      : Number.POSITIVE_INFINITY;
    const bv = Number.isFinite(Number(b?.[field]))
      ? Number(b[field])
      : Number.POSITIVE_INFINITY;
    if (av !== bv) {
      return av - bv;
    }
    return String(a?.competitorName || a?.entryId || "").localeCompare(
      String(b?.competitorName || b?.entryId || ""),
    );
  });
}

function leaderboardRowKey(row) {
  return String(row?.entryId || row?.competitorId || row?.sk || "");
}

function isLeaderboardRowComplete(row) {
  return (
    String(row?.reviewStatus || "")
      .trim()
      .toLowerCase() === "confirmed"
  );
}

function buildShowLeaderboardState(match, matchRows, stageRows) {
  const completedMatchRows = matchRows.filter(isLeaderboardRowComplete);
  const completedStageRows = stageRows.filter(isLeaderboardRowComplete);
  const requiredStageCount =
    Array.isArray(match?.stages) && match.stages.length > 0
      ? match.stages.length
      : new Set(
          completedStageRows
            .map((row) => row?.stageId || row?.matchStageId)
            .filter(Boolean),
        ).size;

  const stageProgressByKey = new Map();
  for (const row of completedStageRows) {
    const key = leaderboardRowKey(row);
    const stageId = row?.stageId || row?.matchStageId;
    if (!key || !stageId) {
      continue;
    }

    if (!stageProgressByKey.has(key)) {
      stageProgressByKey.set(key, new Set());
    }
    stageProgressByKey.get(key).add(stageId);
  }

  const rankedRows =
    requiredStageCount > 0
      ? sortByTimeAsc(
          completedMatchRows.filter(
            (row) =>
              (stageProgressByKey.get(leaderboardRowKey(row))?.size || 0) >=
              requiredStageCount,
          ),
          "totalScore",
        )
      : sortByTimeAsc(completedMatchRows, "totalScore");

  const leaderScore =
    rankedRows.length > 0 ? Number(rankedRows[0]?.totalScore) : null;
  return {
    match,
    rankedRows,
    leaderScore,
  };
}

function NraShowPlaylist() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [leaderboardByMatchId, setLeaderboardByMatchId] = useState({});
  const [readyBannerSrcByMatchId, setReadyBannerSrcByMatchId] = useState({});
  const [displayedLeaderboard, setDisplayedLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leaderboardMinElapsed, setLeaderboardMinElapsed] = useState(false);
  const [leaderboardReachedEnd, setLeaderboardReachedEnd] = useState(false);
  const [videoSegmentByKey, setVideoSegmentByKey] = useState({});
  const videoRef = useRef(null);
  const hasLoadedLeaderboardRef = useRef(false);
  const capturedLeaderboardIndexRef = useRef(-1);

  const activeItem = SHOW_PLAYLIST[activeIndex];
  const activeLeaderboard =
    activeItem?.type === "leaderboard"
      ? leaderboardByMatchId[activeItem.matchId]
      : null;
  const activeBannerSrc =
    activeItem?.type === "leaderboard"
      ? readyBannerSrcByMatchId[activeItem.matchId] ||
        DEFAULT_LEADERBOARD_BANNER_SRC
      : DEFAULT_LEADERBOARD_BANNER_SRC;
  const displayedRows = useMemo(() => {
    if (!displayedLeaderboard) {
      return [];
    }

    if (String(activeItem?.topCount || "").trim().toLowerCase() === "all") {
      return displayedLeaderboard.rankedRows;
    }

    const configuredTopCount = Number(activeItem?.topCount);
    const topCount =
      Number.isFinite(configuredTopCount) && configuredTopCount > 0
        ? Math.floor(configuredTopCount)
        : 20;

    return displayedLeaderboard.rankedRows.slice(0, topCount);
  }, [activeItem?.topCount, displayedLeaderboard]);
  const leaderboardMatchIds = useMemo(
    () => [
      ...new Set(
        SHOW_PLAYLIST.filter((item) => item.type === "leaderboard").map(
          (item) => item.matchId,
        ),
      ),
    ],
    [],
  );

  const advance = useCallback(() => {
    setActiveIndex((current) => (current + 1) % SHOW_PLAYLIST.length);
  }, []);

  const goBack = useCallback(() => {
    setActiveIndex(
      (current) => (current - 1 + SHOW_PLAYLIST.length) % SHOW_PLAYLIST.length,
    );
  }, []);

  const handleLeaderboardReachedEnd = useCallback(() => {
    setLeaderboardReachedEnd(true);
  }, []);

  useEffect(() => {
    let active = true;
    let intervalId = 0;

    async function loadLeaderboards() {
      const isInitialLoad = !hasLoadedLeaderboardRef.current;
      if (isInitialLoad) {
        setLoading(true);
        setError("");
      }

      try {
        const results = await Promise.all(
          leaderboardMatchIds.map(async (matchId) => {
            const [match, matchRows, stageRows] = await Promise.all([
              getMatch(matchId),
              listMatchLeaderboards({ matchId }),
              listStageLeaderboards({ matchId }),
            ]);

            return [
              matchId,
              buildShowLeaderboardState(match, matchRows, stageRows),
            ];
          }),
        );

        if (!active) {
          return;
        }

        setLeaderboardByMatchId(Object.fromEntries(results));
        setError("");
        hasLoadedLeaderboardRef.current = true;
      } catch (loadError) {
        if (!active) {
          return;
        }
        if (!hasLoadedLeaderboardRef.current) {
          setError(loadError?.message || "Failed to load leaderboard data");
        }
      } finally {
        if (active && isInitialLoad) {
          setLoading(false);
        }
      }
    }

    if (activeItem?.type !== "leaderboard") {
      loadLeaderboards();
      intervalId = window.setInterval(loadLeaderboards, 20000);
    }

    return () => {
      active = false;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [activeItem?.type, leaderboardMatchIds]);

  useEffect(() => {
    let active = true;
    const entries = Object.entries(leaderboardByMatchId);

    if (!entries.length) {
      return () => {
        active = false;
      };
    }

    entries.forEach(([matchId, leaderboard]) => {
      const targetBannerSrc = String(
        leaderboard?.match?.leaderboardBannerUrl || "",
      ).trim();

      if (!targetBannerSrc) {
        setReadyBannerSrcByMatchId((prev) =>
          prev[matchId] === DEFAULT_LEADERBOARD_BANNER_SRC
            ? prev
            : { ...prev, [matchId]: DEFAULT_LEADERBOARD_BANNER_SRC },
        );
        return;
      }

      setReadyBannerSrcByMatchId((prev) =>
        prev[matchId] === DEFAULT_LEADERBOARD_BANNER_SRC
          ? prev
          : { ...prev, [matchId]: DEFAULT_LEADERBOARD_BANNER_SRC },
      );

      preloadLeaderboardBanner(targetBannerSrc)
        .then((loadedSrc) => {
          if (!active) {
            return;
          }
          setReadyBannerSrcByMatchId((prev) =>
            prev[matchId] === loadedSrc
              ? prev
              : { ...prev, [matchId]: loadedSrc },
          );
        })
        .catch(() => {
          if (!active) {
            return;
          }
          setReadyBannerSrcByMatchId((prev) =>
            prev[matchId] === DEFAULT_LEADERBOARD_BANNER_SRC
              ? prev
              : { ...prev, [matchId]: DEFAULT_LEADERBOARD_BANNER_SRC },
          );
        });
    });

    return () => {
      active = false;
    };
  }, [leaderboardByMatchId]);

  useEffect(() => {
    if (
      !activeItem ||
      activeItem.type === "video" ||
      activeItem.type === "leaderboard"
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      advance,
      activeItem.durationMs || 10000,
    );
    return () => window.clearTimeout(timeoutId);
  }, [activeItem, advance]);

  useEffect(() => {
    setLeaderboardMinElapsed(false);
    setLeaderboardReachedEnd(false);
  }, [activeIndex]);

  useEffect(() => {
    if (activeItem?.type !== "leaderboard") {
      capturedLeaderboardIndexRef.current = -1;
      setDisplayedLeaderboard(null);
      return;
    }

    if (loading || error || !activeLeaderboard) {
      return;
    }

    if (capturedLeaderboardIndexRef.current !== activeIndex) {
      setDisplayedLeaderboard(activeLeaderboard);
      capturedLeaderboardIndexRef.current = activeIndex;
    }
  }, [activeIndex, activeItem, activeLeaderboard, error, loading]);

  useEffect(() => {
    if (!activeItem || activeItem.type !== "leaderboard") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setLeaderboardMinElapsed(true);
    }, activeItem.durationMs || 10000);
    return () => window.clearTimeout(timeoutId);
  }, [activeItem]);

  useEffect(() => {
    if (activeItem?.type !== "leaderboard") {
      return;
    }

    const shouldWaitForScrollEnd =
      !loading && !error && Boolean(displayedLeaderboard);
    if (
      leaderboardMinElapsed &&
      (!shouldWaitForScrollEnd || leaderboardReachedEnd)
    ) {
      advance();
    }
  }, [
    activeItem,
    advance,
    displayedLeaderboard,
    error,
    leaderboardMinElapsed,
    leaderboardReachedEnd,
    loading,
  ]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "ArrowRight") {
        advance();
      } else if (event.key === "ArrowLeft") {
        goBack();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [advance, goBack]);

  useEffect(() => {
    if (activeItem?.type !== "video" || !videoRef.current) {
      return;
    }

    const video = videoRef.current;
    const activeSegment = videoSegmentByKey[activeItem.key] || 0;
    let completed = false;

    const finishSegment = () => {
      if (completed) {
        return;
      }
      completed = true;
      setVideoSegmentByKey((prev) => ({
        ...prev,
        [activeItem.key]: (activeSegment + 1) % 3,
      }));
      advance();
    };

    const configureSegment = () => {
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) {
        const playback = video.play();
        if (playback && typeof playback.catch === "function") {
          playback.catch(() => {});
        }
        return;
      }

      const segmentLength = duration / 3;
      const boundedSegment =
        activeSegment >= 0 && activeSegment <= 2 ? activeSegment : 0;
      const segmentStart = segmentLength * boundedSegment;
      const segmentEnd =
        boundedSegment === 2 ? duration : segmentStart + segmentLength;
      video.currentTime = segmentStart;

      const playback = video.play();
      if (playback && typeof playback.catch === "function") {
        playback.catch(() => {});
      }

      const onTimeUpdate = () => {
        if (video.currentTime >= Math.max(segmentEnd - 0.08, segmentStart)) {
          video.pause();
          finishSegment();
        }
      };

      video.addEventListener("timeupdate", onTimeUpdate);
      cleanupFns.push(() =>
        video.removeEventListener("timeupdate", onTimeUpdate),
      );
    };

    const cleanupFns = [];
    const onLoadedMetadata = () => {
      configureSegment();
    };
    const onEnded = () => {
      finishSegment();
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("ended", onEnded);
    cleanupFns.push(() =>
      video.removeEventListener("loadedmetadata", onLoadedMetadata),
    );
    cleanupFns.push(() => video.removeEventListener("ended", onEnded));

    if (Number.isFinite(video.duration) && video.duration > 0) {
      configureSegment();
    } else {
      video.load();
    }

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [activeItem, advance, videoSegmentByKey]);

  return (
    <section className="show-playlist-page">
      <Helmet>
        <title>NRA Show Playlist | Range Pocket Competition</title>
      </Helmet>

      <div className="show-playlist-stage">
        {activeItem?.type === "image" && (
          <div className="show-media-slide show-media-slide--framed">
            <div className="show-media-frame">
              <div className="show-media-frame-label">
                <span>Range Pocket Display</span>
                <strong>{activeItem.label || "Booth slide"}</strong>
              </div>
              <img
                className="show-media-asset"
                src={activeItem.src}
                alt={activeItem.label || "Range Pocket booth slide"}
              />
              {activeItem.caption && (
                <div className="show-media-caption">{activeItem.caption}</div>
              )}
            </div>
          </div>
        )}

        {activeItem?.type === "video" && (
          <div className="show-media-slide">
            <video
              key={activeItem.key}
              ref={videoRef}
              className="show-media-asset"
              src={activeItem.src}
              autoPlay
              muted
              playsInline
            />
          </div>
        )}

        {activeItem?.type === "leaderboard" && loading && (
          <div className="show-playlist-loading">
            <span className="owner-registration-section-label">Loading</span>
            <strong>Leaderboard</strong>
          </div>
        )}

        {activeItem?.type === "leaderboard" && !loading && error && (
          <div className="show-playlist-error">{error}</div>
        )}

        {activeItem?.type === "leaderboard" &&
          !loading &&
          !error &&
          displayedLeaderboard && (
            <BoothLeaderboardDisplay
              match={displayedLeaderboard.match}
              rows={displayedRows}
              topCount="all"
              onTopCountChange={() => {}}
              showControls={false}
              leaderScore={displayedLeaderboard.leaderScore}
              bannerSrc={activeBannerSrc}
              onReachedEnd={handleLeaderboardReachedEnd}
            />
          )}
      </div>
    </section>
  );
}

export const NraShowPlaylist_Page = {
  path: "/matches/show/nra",
  element: <NraShowPlaylist />,
};
